import { Group, List, Paper, Stack, ThemeIcon } from "@mantine/core";

import { IconInfoCircle } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import {
  LABOR_AMEND_PENDING_CHANGES_MESSAGE,
  LABOR_AMEND_PENDING_CHANGES_TITLE,
} from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import type { LaborAmendDisplayDetail } from "@/encounters/components/EncounterLabor/types/labor-amend";
import { getAmendDisplayDetails } from "@/encounters/components/EncounterLabor/utils/labor-amend-preview";
import { useLaborAmendFlowsheetPersist } from "@/encounters/contexts/LaborAmendFlowsheetPersistContext";

function PendingChangeDetailRow({ detail }: { detail: LaborAmendDisplayDetail }) {
  if (detail.isGroupHeading) {
    return (
      <TextWrapper fw={600} size="sm" pl={detail.indent ? "md" : 0}>
        {detail.label}
      </TextWrapper>
    );
  }

  return (
    <Group
      gap={4}
      wrap="wrap"
      align="baseline"
      pl={detail.indent ? "md" : 0}
      style={detail.indent ? { borderLeft: "2px solid var(--mantine-color-gray-3)" } : undefined}
    >
      <TextWrapper span size="sm" c="dimmed">
        {detail.label}:
      </TextWrapper>
      <TextWrapper span size="sm">
        {detail.value}
      </TextWrapper>
    </Group>
  );
}

export default function LaborAmendPendingChangesBanner() {
  const { deferFlowsheetPersists, pendingDisplayEntries, hasLocalAmendPreviews } =
    useLaborAmendFlowsheetPersist();

  if (!deferFlowsheetPersists || !hasLocalAmendPreviews) {
    return null;
  }

  const hasList = pendingDisplayEntries.length > 0;

  return (
    <Paper
      p="lg"
      radius="md"
      bg="orange.0"
      style={{ border: "1px solid var(--mantine-color-orange-2)" }}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon size={28} radius="xl" aria-hidden color="orange.8" variant="transparent">
          <IconInfoCircle size={22} stroke={2} />
        </ThemeIcon>
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          <TextWrapper fw={700} size="sm" c="orange.8">
            {LABOR_AMEND_PENDING_CHANGES_TITLE}
          </TextWrapper>
          <TextWrapper size="sm">{LABOR_AMEND_PENDING_CHANGES_MESSAGE}</TextWrapper>
          {hasList ? (
            <List size="sm" withPadding spacing="xs">
              {pendingDisplayEntries.map((entry) => {
                const details = getAmendDisplayDetails(entry);

                return (
                  <List.Item key={entry.key}>
                    <Stack gap={4}>
                      <TextWrapper fw={700} size="sm">
                        {entry.title}
                      </TextWrapper>
                      {details.length > 0 ? (
                        <Stack gap={0} pl="xs">
                          {details.map((detail, detailIndex) => (
                            <PendingChangeDetailRow
                              detail={detail}
                              key={`${entry.key}-${detail.label}-${detailIndex}`}
                            />
                          ))}
                        </Stack>
                      ) : null}
                    </Stack>
                  </List.Item>
                );
              })}
            </List>
          ) : null}
        </Stack>
      </Group>
    </Paper>
  );
}
